import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";
import Button from "../components/Button";
import Input from "../components/Input";
import ToggleSwitch from "../components/ToggleSwitch";
import TwoFASetup from "../components/TwoFASetup";
import { UserIcon, MailIcon, PhoneIcon, WalletIcon } from "../components/Icons";
import { userAPI } from "../services/api";

const ProfileSecurity = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [currentStep, setCurrentStep] = useState(1);

  // Account Overview State
  const [userInfo, setUserInfo] = useState({
    name: "",
    email: "",
    avatar: null,
  });

  // 2FA State
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FADisable, setShow2FADisable] = useState(false);
  const [disable2FAToken, setDisable2FAToken] = useState("");

  // Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Contact Information State
  const [contactInfo, setContactInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
  });

  // Nominee Information State
  const [nomineeInfo, setNomineeInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
  });

  // Withdrawal Information State
  const [withdrawalInfo, setWithdrawalInfo] = useState({
    walletName: "",
    withdrawalMethod: "Bitcoin (BTC)",
    destinationAddress: "",
  });

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getProfile();

      setUserInfo({
        name: response.name || "",
        email: response.email || "",
        avatar: response.avatar || null,
      });

      setContactInfo({
        fullName: response.name || "",
        email: response.email || "",
        phone: response.phone || "",
        address: response.address || "",
      });

      const twoFAStatus =
        typeof response.twoFAEnabled === "boolean"
          ? response.twoFAEnabled
          : Boolean(response?.user?.twoFAEnabled);
      setTwoFactorEnabled(twoFAStatus);

      setNomineeInfo({
        fullName: response.nomineeName || "",
        email: response.nomineeEmail || "",
        phone: response.nomineePhone || "",
        address: response.nomineeAddress || "",
      });

      setWithdrawalInfo({
        walletName: response.walletName || "",
        withdrawalMethod: response.withdrawalMethod || "Bitcoin (BTC)",
        destinationAddress: response.destinationAddress || "",
      });
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      showMessage("error", "Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const handlePasswordUpdate = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      showMessage("error", "Please fill in all password fields");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage("error", "New passwords do not match");
      return;
    }

    try {
      setSaving(true);
      await userAPI.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword,
      );
      showMessage("success", "Password updated successfully");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      showMessage("error", error.error || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  const handleContactSave = async () => {
    try {
      setSaving(true);
      await userAPI.updateProfile({
        name: contactInfo.fullName,
        email: contactInfo.email,
        phone: contactInfo.phone,
        address: contactInfo.address,
      });
      showMessage("success", "Contact information updated successfully");
      setUserInfo({
        ...userInfo,
        name: contactInfo.fullName,
        email: contactInfo.email,
      });
      await fetchUserData();
    } catch (error) {
      showMessage("error", error.error || "Failed to update contact info");
    } finally {
      setSaving(false);
    }
  };

  const handleNomineeSave = async () => {
    try {
      setSaving(true);
      await userAPI.updateNominee({
        nomineeName: nomineeInfo.fullName,
        nomineeEmail: nomineeInfo.email,
        nomineePhone: nomineeInfo.phone,
        nomineeAddress: nomineeInfo.address,
      });
      showMessage("success", "Nominee information updated successfully");
      await fetchUserData();
    } catch (error) {
      showMessage("error", error.error || "Failed to update nominee info");
    } finally {
      setSaving(false);
    }
  };

  const handleWithdrawalSave = async () => {
    try {
      setSaving(true);
      await userAPI.updateWithdrawal({
        walletName: withdrawalInfo.walletName,
        withdrawalMethod: withdrawalInfo.withdrawalMethod,
        destinationAddress: withdrawalInfo.destinationAddress,
      });
      showMessage("success", "Withdrawal information updated successfully");
      await fetchUserData();
    } catch (error) {
      showMessage("error", error.error || "Failed to update withdrawal info");
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = () => {
    showMessage("info", "Avatar upload feature coming soon");
  };

  const handle2FAToggle = async (enabled) => {
    if (enabled) {
      // Show setup modal
      setShow2FASetup(true);
    } else {
      // Show disable confirmation
      setShow2FADisable(true);
    }
  };

  const handle2FASetupSuccess = () => {
    setTwoFactorEnabled(true);
    setShow2FASetup(false);
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      localStorage.setItem(
        "user",
        JSON.stringify({ ...user, twoFAEnabled: true }),
      );
    }
    fetchUserData();
    showMessage("success", "2FA enabled successfully!");
  };

  const handle2FADisable = async () => {
    if (disable2FAToken.length !== 6) {
      showMessage("error", "Please enter a valid 6-digit code");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const response = await fetch(
        "http://localhost:5000/api/auth/2fa/disable",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token: disable2FAToken }),
        },
      );

      const data = await response.json();

      if (data.success) {
        setTwoFactorEnabled(false);
        setShow2FADisable(false);
        setDisable2FAToken("");
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          localStorage.setItem(
            "user",
            JSON.stringify({ ...user, twoFAEnabled: false }),
          );
        }
        fetchUserData();
        showMessage("success", "2FA disabled successfully");
      } else {
        showMessage("error", data.error || "Failed to disable 2FA");
      }
    } catch (error) {
      showMessage("error", "Failed to disable 2FA");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[var(--bg-main)] overflow-x-hidden">
        <Sidebar />
        <div className="flex-1 min-w-0 p-4 md:p-8 lg:ml-64 overflow-x-hidden">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  const steps = [
    { number: 1, title: "Contact Information", desc: "Personal details" },
    { number: 2, title: "Nominee Information", desc: "Emergency contact" },
    { number: 3, title: "Withdrawal Information", desc: "Payment details" },
  ];

  const goToStep = (stepNumber) => {
    setCurrentStep(stepNumber);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Full Name
              </label>
              <input
                type="text"
                value={contactInfo.fullName}
                onChange={(e) =>
                  setContactInfo({ ...contactInfo, fullName: e.target.value })
                }
                className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={contactInfo.email}
                onChange={(e) =>
                  setContactInfo({ ...contactInfo, email: e.target.value })
                }
                className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={contactInfo.phone}
                onChange={(e) =>
                  setContactInfo({ ...contactInfo, phone: e.target.value })
                }
                className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Address
              </label>
              <input
                type="text"
                value={contactInfo.address}
                onChange={(e) =>
                  setContactInfo({ ...contactInfo, address: e.target.value })
                }
                className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Nominee Full Name
              </label>
              <input
                type="text"
                value={nomineeInfo.fullName}
                onChange={(e) =>
                  setNomineeInfo({ ...nomineeInfo, fullName: e.target.value })
                }
                className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Nominee Email Address
              </label>
              <input
                type="email"
                value={nomineeInfo.email}
                onChange={(e) =>
                  setNomineeInfo({ ...nomineeInfo, email: e.target.value })
                }
                className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Nominee Phone Number
              </label>
              <input
                type="tel"
                value={nomineeInfo.phone}
                onChange={(e) =>
                  setNomineeInfo({ ...nomineeInfo, phone: e.target.value })
                }
                className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Nominee Address
              </label>
              <input
                type="text"
                value={nomineeInfo.address}
                onChange={(e) =>
                  setNomineeInfo({ ...nomineeInfo, address: e.target.value })
                }
                className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Wallet Name
              </label>
              <input
                type="text"
                value={withdrawalInfo.walletName}
                onChange={(e) =>
                  setWithdrawalInfo({
                    ...withdrawalInfo,
                    walletName: e.target.value,
                  })
                }
                className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Withdrawal Method
              </label>
              <select
                value={withdrawalInfo.withdrawalMethod}
                onChange={(e) =>
                  setWithdrawalInfo({
                    ...withdrawalInfo,
                    withdrawalMethod: e.target.value,
                  })
                }
                className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
              >
                <option>USDT-TRC20 (Tron Network)</option>
                <option>USDT-ERC20 (Ethereum Network)</option>
                <option>USDT-BEP20 (BSC Network)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Destination Address / Account Number
              </label>
              <input
                type="text"
                value={withdrawalInfo.destinationAddress}
                onChange={(e) =>
                  setWithdrawalInfo({
                    ...withdrawalInfo,
                    destinationAddress: e.target.value,
                  })
                }
                placeholder="Enter wallet address"
                className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const handleNext = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSave = async () => {
    switch (currentStep) {
      case 1:
        await handleContactSave();
        break;
      case 2:
        await handleNomineeSave();
        break;
      case 3:
        await handleWithdrawalSave();
        break;
      default:
        break;
    }
  };

  const handleCompleteProfile = async () => {
    try {
      setSaving(true);
      await userAPI.updateProfile({
        name: contactInfo.fullName,
        email: contactInfo.email,
        phone: contactInfo.phone,
        address: contactInfo.address,
      });
      await userAPI.updateNominee({
        nomineeName: nomineeInfo.fullName,
        nomineeEmail: nomineeInfo.email,
        nomineePhone: nomineeInfo.phone,
        nomineeAddress: nomineeInfo.address,
      });
      await userAPI.updateWithdrawal({
        walletName: withdrawalInfo.walletName,
        withdrawalMethod: withdrawalInfo.withdrawalMethod,
        destinationAddress: withdrawalInfo.destinationAddress,
      });
      await fetchUserData();
      showMessage("success", "All profile details saved successfully");
    } catch (error) {
      showMessage("error", error.error || "Failed to complete profile save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)] overflow-x-hidden">
      <Sidebar />
      <div className="flex-1 min-w-0 p-4 md:p-8 lg:ml-64 overflow-x-hidden">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Profile & Security
          </h1>
          <p className="text-[var(--text-primary)] text-sm md:text-base">
            Manage your account settings and security preferences
          </p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-xl border ${
              message.type === "success"
                ? "bg-green-500/10 border-green-500/30 text-green-400"
                : message.type === "error"
                  ? "bg-red-500/10 border-red-500/30 text-red-400"
                  : "bg-blue-500/10 border-blue-500/30 text-blue-400"
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="space-y-6">
          {/* 1. Account Overview Card */}
          <Card>
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                Account Overview
              </h2>
              <button className="p-2 hover:bg-[#22c55e]/10 rounded-lg transition-all">
                <svg
                  className="w-5 h-5 text-[var(--text-primary)]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </button>
            </div>

            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-[#22c55e] to-[#16a34a] flex items-center justify-center text-4xl md:text-5xl font-bold text-white">
                  {userInfo.name.charAt(0)}
                </div>
              </div>

              {/* User Info */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] mb-2">
                  {userInfo.name}
                </h3>
                <p className="text-[var(--text-primary)] opacity-70 mb-4">{userInfo.email}</p>
                <button
                  onClick={handleAvatarChange}
                  className="px-6 py-2.5 rounded-xl border-2 border-primary text-[var(--text-primary)] hover:bg-[#22c55e]/10 transition-all font-semibold"
                >
                  Change Avatar
                </button>
              </div>
            </div>
          </Card>

          {/* 2. Two-Factor Authentication Card */}
          <Card>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                  Two-Factor Authentication
                </h2>
                <p className="text-[var(--text-primary)] text-sm md:text-base mb-2">
                  Enable 2FA for enhanced security
                </p>
                <p className="text-[var(--text-primary)] text-sm opacity-70">
                  Two-factor authentication is currently{" "}
                  {twoFactorEnabled ? "enabled" : "disabled"}.
                </p>
              </div>
              <div className="flex justify-center md:justify-end">
                <ToggleSwitch
                  enabled={twoFactorEnabled}
                  onChange={handle2FAToggle}
                />
              </div>
            </div>
          </Card>

          {/* 3. Change Password Card */}
          <Card>
            <h2 className="text-xl md:text-2xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Change Password
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      currentPassword: e.target.value,
                    })
                  }
                  placeholder="Enter current password"
                  className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      newPassword: e.target.value,
                    })
                  }
                  placeholder="Enter new password"
                  className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      confirmPassword: e.target.value,
                    })
                  }
                  placeholder="Confirm new password"
                  className="w-full px-4 py-3 rounded-xl bg-surface-soft border border-[#1F2937] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary focus:ring-2 focus:ring-[#22c55e]/20 transition-all"
                />
              </div>

              <button
                onClick={handlePasswordUpdate}
                disabled={saving}
                className="w-full md:w-auto px-8 py-3 rounded-xl bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-bold hover:shadow-lg hover:shadow-primary/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Updating..." : "Update Password"}
              </button>
            </div>
          </Card>

          {/* Profile Information Stepper */}
          <Card>
            {/* Stepper Header */}
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                Complete Your Profile
              </h2>

              {/* Stepper Indicator */}
              <div className="flex items-center justify-between relative mb-8">
                {/* Progress Line */}
                <div className="absolute top-5 left-0 right-0 h-1 bg-surface-soft -z-10">
                  <div
                    className="h-full bg-gradient-to-r from-[#22c55e] to-[#16a34a] transition-all duration-300"
                    style={{ width: `${((currentStep - 1) / 2) * 100}%` }}
                  />
                </div>

                {/* Steps */}
                {steps.map((step) => (
                  <button
                    key={step.number}
                    type="button"
                    onClick={() => goToStep(step.number)}
                    className="flex flex-col items-center flex-1"
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                        currentStep >= step.number
                          ? "bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white"
                          : "bg-surface-soft text-[#A1A1AA]"
                      }`}
                    >
                      {step.number}
                    </div>
                    <div className="mt-2 text-center">
                      <p
                        className={`text-xs md:text-sm font-semibold transition-all ${
                          currentStep >= step.number
                            ? "text-[var(--text-primary)]"
                            : "text-[#A1A1AA]"
                        }`}
                      >
                        {step.title}
                      </p>
                      <p className="text-xs text-[#A1A1AA] hidden md:block">
                        {step.desc}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Step Content */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
                {steps[currentStep - 1].title}
              </h3>
              {renderStepContent()}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-6 border-t border-[#1F2937]">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 1}
                className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                  currentStep === 1
                    ? "opacity-0 cursor-not-allowed"
                    : "bg-surface-soft text-[var(--text-primary)] hover:bg-surface-soft"
                }`}
              >
                Previous
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-3 rounded-xl bg-surface-soft text-[var(--text-primary)] font-semibold hover:bg-surface-soft transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? "Saving..." : "Save Progress"}
                </button>

                {currentStep < 3 ? (
                  <button
                    onClick={handleNext}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-bold hover:shadow-lg hover:shadow-primary/50 transition-all"
                  >
                    Next Step
                  </button>
                ) : (
                  <button
                    onClick={handleCompleteProfile}
                    disabled={saving}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-bold hover:shadow-lg hover:shadow-primary/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? "Completing..." : "Complete Profile"}
                  </button>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center py-8 mt-8">
          <p className="text-sm text-[var(--text-primary)] opacity-60">
            © 2026 Aiarbix. All rights reserved.
          </p>
        </div>
      </div>

      {/* 2FA Setup Modal */}
      {show2FASetup && (
        <div className="fixed inset-0 bg-overlay flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="relative max-w-5xl w-full my-8">
            <button
              onClick={() => {
                setShow2FASetup(false);
                setTwoFactorEnabled(false);
              }}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-surface-soft hover:bg-surface-soft text-primary flex items-center justify-center transition-all"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <TwoFASetup user={userInfo} onSuccess={handle2FASetupSuccess} />
          </div>
        </div>
      )}

      {/* 2FA Disable Modal */}
      {show2FADisable && (
        <div className="fixed inset-0 bg-overlay flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] rounded-2xl p-8 max-w-md w-full border-2 border-primary/20">
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-10 h-10 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">Disable 2FA</h2>
              <p className="text-[#A1A1AA] text-sm">
                Enter your authenticator code to disable two-factor
                authentication
              </p>
            </div>

            <div className="mb-6">
              <input
                type="text"
                value={disable2FAToken}
                onChange={(e) =>
                  setDisable2FAToken(e.target.value.replace(/\D/g, ""))
                }
                placeholder="000000"
                maxLength="6"
                className="w-full px-4 py-4 rounded-xl bg-surface-soft border-2 border-red-500 text-primary text-center text-3xl font-bold tracking-[0.5em] focus:outline-none focus:ring-4 focus:ring-red-500/20 transition-all placeholder:text-[var(--text-secondary)]/40"
              />
            </div>

            <div className="space-y-3">
              <button
                onClick={handle2FADisable}
                disabled={saving || disable2FAToken.length !== 6}
                className="w-full px-6 py-4 rounded-xl bg-red-500 text-white font-bold text-lg hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Disabling..." : "Disable 2FA"}
              </button>
              <button
                onClick={() => {
                  setShow2FADisable(false);
                  setDisable2FAToken("");
                  setTwoFactorEnabled(true);
                }}
                className="w-full px-6 py-4 rounded-xl bg-surface-soft text-primary font-semibold hover:bg-surface-soft transition-all"
              >
                Cancel
              </button>
            </div>

            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-yellow-500 text-xs text-center">
                ⚠️ Disabling 2FA will make your account less secure
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSecurity;
